import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generateSectionReportPDF, 
  generatePDFFromElement 
} from "../../../utils/invoiceUtils";

const ViewComplaintReportModal = ({ 
  isOpen, 
  onClose, 
  campusId,
  teacherId, 
  sectionId, 
  role,
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy data for complaints - Admin view (campus-wide)
  const dummyAdminComplaintReport = {
    complaints: [
      {
        id: "CF-001",
        submittedBy: "John Doe",
        dateSubmitted: "2024-01-15",
        createdAt: "2024-01-15T10:30:00Z",
        category: "Academic",
        type: "Academic",
        subject: "Course Material Issue",
        description: "The course materials are not accessible through the online portal. Need immediate assistance.",
        details: "The course materials are not accessible through the online portal. Need immediate assistance.",
        status: "pending",
        priority: "high",
        resolutionDate: null,
        staffTeacher: "Dr. Smith",
        assignedTo: "Dr. Smith"
      },
      {
        id: "CF-002",
        submittedBy: "Jane Smith",
        dateSubmitted: "2024-01-14",
        createdAt: "2024-01-14T14:20:00Z",
        category: "Administrative",
        type: "Administrative",
        subject: "Fee Payment Issue",
        description: "Having trouble with fee payment through the online system.",
        details: "Having trouble with fee payment through the online system.",
        status: "in progress",
        priority: "medium",
        resolutionDate: null,
        staffTeacher: "Admin Office",
        assignedTo: "Admin Office"
      },
      {
        id: "CF-003",
        submittedBy: "Mike Johnson",
        dateSubmitted: "2024-01-13",
        createdAt: "2024-01-13T09:15:00Z",
        category: "Technical",
        type: "Technical",
        subject: "Login Problems",
        description: "Cannot access my student portal account.",
        details: "Cannot access my student portal account.",
        status: "resolved",
        priority: "low",
        resolutionDate: "2024-01-14",
        staffTeacher: "IT Support",
        assignedTo: "IT Support"
      }
    ]
  };

  // Dummy data for teacher-specific complaints
  const dummyTeacherComplaintReport = {
    complaints: [
      {
        id: "CF-T001",
        submittedBy: "Sarah Wilson",
        dateSubmitted: "2024-01-16",
        createdAt: "2024-01-16T11:15:00Z",
        category: "Academic",
        type: "Academic",
        subject: "Assignment Feedback Request",
        description: "Could you please provide more detailed feedback on my recent mathematics assignment? I would like to understand where I went wrong.",
        details: "Could you please provide more detailed feedback on my recent mathematics assignment? I would like to understand where I went wrong.",
        status: "pending",
        priority: "medium",
        resolutionDate: null,
        staffTeacher: "Mr. Johnson",
        assignedTo: "Mr. Johnson"
      },
      {
        id: "CF-T002",
        submittedBy: "David Brown",
        dateSubmitted: "2024-01-15",
        createdAt: "2024-01-15T14:30:00Z",
        category: "Academic",
        type: "Academic",
        subject: "Class Schedule Conflict",
        description: "I have a conflict with the scheduled class time. Can we discuss alternative arrangements?",
        details: "I have a conflict with the scheduled class time. Can we discuss alternative arrangements?",
        status: "in progress",
        priority: "high",
        resolutionDate: null,
        staffTeacher: "Mr. Johnson",
        assignedTo: "Mr. Johnson"
      },
      {
        id: "CF-T003",
        submittedBy: "Emily Davis",
        dateSubmitted: "2024-01-14",
        createdAt: "2024-01-14T09:45:00Z",
        category: "Academic",
        type: "Academic",
        subject: "Extra Help Request",
        description: "I'm struggling with the calculus concepts. Could you please schedule some extra help sessions?",
        details: "I'm struggling with the calculus concepts. Could you please schedule some extra help sessions?",
        status: "resolved",
        priority: "medium",
        resolutionDate: "2024-01-15",
        staffTeacher: "Mr. Johnson",
        assignedTo: "Mr. Johnson"
      },
      {
        id: "CF-T004",
        submittedBy: "Alex Thompson",
        dateSubmitted: "2024-01-13",
        createdAt: "2024-01-13T16:20:00Z",
        category: "Academic",
        type: "Academic",
        subject: "Grade Clarification",
        description: "I would like to discuss my recent test grade and understand how to improve for the next exam.",
        details: "I would like to discuss my recent test grade and understand how to improve for the next exam.",
        status: "resolved",
        priority: "low",
        resolutionDate: "2024-01-14",
        staffTeacher: "Mr. Johnson",
        assignedTo: "Mr. Johnson"
      }
    ]
  };

  // Dummy section reports for admin view
  const dummyAdminSectionReports = [
    {
      complaints: [
        {
          id: "CF-004",
          submittedBy: "Alice Brown",
          createdAt: "2024-01-12T11:45:00Z",
          type: "Academic",
          subject: "Assignment Feedback",
          description: "Need clarification on assignment feedback provided by the teacher.",
          status: "pending",
          priority: "medium"
        },
        {
          id: "CF-005",
          submittedBy: "Bob Wilson",
          createdAt: "2024-01-11T16:30:00Z",
          type: "Administrative",
          subject: "Attendance Record",
          description: "My attendance record seems incorrect for last week.",
          status: "in progress",
          priority: "high"
        }
      ]
    }
  ];

  // Dummy section reports for teacher view
  const dummyTeacherSectionReports = [
    {
      complaints: [
        {
          id: "CF-TS001",
          submittedBy: "Lisa Martinez",
          createdAt: "2024-01-16T10:30:00Z",
          type: "Academic",
          subject: "Homework Help Request",
          description: "I need help understanding the quadratic equations from yesterday's lesson.",
          status: "pending",
          priority: "medium"
        },
        {
          id: "CF-TS002",
          submittedBy: "Tom Anderson",
          createdAt: "2024-01-15T15:45:00Z",
          type: "Academic",
          subject: "Test Schedule Inquiry",
          description: "When is the next mathematics test scheduled? I want to prepare accordingly.",
          status: "resolved",
          priority: "low"
        },
        {
          id: "CF-TS003",
          submittedBy: "Maria Garcia",
          createdAt: "2024-01-14T13:20:00Z",
          type: "Academic",
          subject: "Study Group Request",
          description: "Can we organize a study group for the upcoming calculus exam?",
          status: "in progress",
          priority: "medium"
        }
      ]
    }
  ];

  // Select appropriate dummy data based on role
  const complaintReport = role === "teacher" ? dummyTeacherComplaintReport : dummyAdminComplaintReport;
  const sectionReports = role === "teacher" ? dummyTeacherSectionReports : dummyAdminSectionReports;

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const downloadPDF = () => {
    if (isSectionReport) {
      generateSectionReportPDF(
        sectionReports,
        className,
        sectionName,
        campusName,
        "Section_Complaint_Report.pdf"
      );
    } else {
      // Use generatePDFFromElement for individual complaint reports
      generatePDFFromElement(reportRef, "Complaint_Report.pdf");
    }
  };

  const downloadExcel = () => {
    if (isSectionReport) {
      if (!sectionReports || !sectionReports.length) return;
      
      const workbook = XLSX.utils.book_new();
      const summaryData = [
        ["Complaints & Feedbacks Section Report"],
        ["Campus", campusName],
        ["Class", className],
        ["Section", sectionName],
        ["Report Type", role === "admin" ? "Administrative View" : "Teacher View"],
        [],
        ["Date", "Type", "Subject", "Description", "Status", "Priority", "Submitted By"],
      ];
      
      sectionReports.forEach(report => {
        if (report.complaints && report.complaints.length > 0) {
          report.complaints.forEach(complaint => {
            summaryData.push([
              new Date(complaint.createdAt).toLocaleDateString('en-GB'),
              complaint.type || "N/A",
              complaint.subject || "N/A",
              complaint.description || "N/A",
              complaint.status || "Pending",
              complaint.priority || "Medium",
              complaint.submittedBy || "Anonymous"
            ]);
          });
        }
      });
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      XLSX.writeFile(workbook, "Section_Complaint_Report.xlsx");
    } else {
      if (!complaintReport) return;

      const report = Array.isArray(complaintReport) ? complaintReport[0] : complaintReport;
      if (!report) return;

      const worksheetData = [
        ["Complaints & Feedbacks Report"],
        ["Campus", campusName],
        ["Report Type", role === "admin" ? "Campus-wide Administrative Report" : "Teacher-specific Report"],
        ["Generated On", new Date().toLocaleDateString('en-GB')],
        [],
        ["Date", "Type", "Subject", "Description", "Status", "Priority", "Submitted By", "Assigned To"],
      ];

      if (report.complaints && Array.isArray(report.complaints)) {
        worksheetData.push(
          ...report.complaints.map((complaint) => [
            new Date(complaint.createdAt).toLocaleDateString('en-GB'),
            complaint.type || "N/A",
            complaint.subject || "N/A",
            complaint.description || "N/A",
            complaint.status || "Pending",
            complaint.priority || "Medium",
            complaint.submittedBy || "Anonymous",
            complaint.assignedTo || "Unassigned"
          ])
        );
      }

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Complaint Report");
      XLSX.writeFile(workbook, "Complaint_Report.xlsx");
    }
  };

  if (!isOpen) return null;

  const reportData = !isSectionReport && complaintReport ? (Array.isArray(complaintReport) ? complaintReport[0] : complaintReport) : null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'resolved': return 'text-green-600 bg-green-50';
      case 'in progress': return 'text-yellow-600 bg-yellow-50';
      case 'pending': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading report...</p>}

        {isSectionReport ? (
          <>
            {sectionReports && sectionReports.length > 0 ? (
              <div>
                {/* Section Report Header */}
                <div className="text-center mb-6 border-b pb-4">
                  <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
                  <h2 className="text-xl font-semibold mt-2">Complaints & Feedbacks Section Report</h2>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div><strong>Class:</strong> {className}</div>
                    <div><strong>Section:</strong> {sectionName}</div>
                    <div><strong>Report Type:</strong> {role === "admin" ? "Administrative View" : "Teacher View"}</div>
                    <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
                  </div>
                </div>

                {/* Complaints Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Summary</h3>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="bg-blue-50 p-4 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {sectionReports.reduce((total, report) => total + (report.complaints?.length || 0), 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Complaints</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded">
                      <div className="text-2xl font-bold text-red-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.complaints?.filter(c => c.status === 'pending')?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Pending</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded">
                      <div className="text-2xl font-bold text-yellow-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.complaints?.filter(c => c.status === 'in progress')?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">In Progress</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded">
                      <div className="text-2xl font-bold text-green-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.complaints?.filter(c => c.status === 'resolved')?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Resolved</div>
                    </div>
                  </div>
                </div>

                {/* Detailed Complaints List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Detailed Report</h3>
                  {sectionReports.map((report, reportIndex) => (
                    <div key={reportIndex}>
                      {report.complaints && report.complaints.length > 0 ? (
                        report.complaints.map((complaint, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 hover:shadow-md transition-shadow">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-semibold text-gray-800">
                                    {complaint.subject || "No Subject"}
                                  </h4>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(complaint.priority)}`}>
                                    {complaint.priority || "Medium"}
                                  </span>
                                </div>
                                <p className="text-gray-600 text-sm mb-2">
                                  <strong>Type:</strong> {complaint.type || "General"}
                                </p>
                                <p className="text-gray-700 text-sm">
                                  {complaint.description || "No description provided"}
                                </p>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-gray-500">Status:</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(complaint.status)}`}>
                                    {complaint.status || "Pending"}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-500">
                                  <strong>Submitted By:</strong> {complaint.submittedBy || "Anonymous"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  <strong>Date:</strong> {new Date(complaint.createdAt).toLocaleDateString('en-GB')}
                                </div>
                                {complaint.assignedTo && (
                                  <div className="text-sm text-gray-500">
                                    <strong>Assigned To:</strong> {complaint.assignedTo}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No complaints found for this section
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No reports found for this section</p>
              </div>
            )}
          </>
        ) : reportData ? (
          <>
            {/* Individual Report Header */}
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
              <h2 className="text-xl font-semibold mt-2">
                Complaints & Feedbacks Report
              </h2>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div><strong>Report Type:</strong> {role === "admin" ? "Campus-wide Administrative Report" : "Teacher-specific Report"}</div>
                <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
              </div>
            </div>

            {/* Report Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded">
                  <div className="text-2xl font-bold text-blue-600">
                    {reportData.complaints?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Complaints</div>
                </div>
                <div className="bg-red-50 p-4 rounded">
                  <div className="text-2xl font-bold text-red-600">
                    {reportData.complaints?.filter(c => c.status === 'pending')?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Pending</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded">
                  <div className="text-2xl font-bold text-yellow-600">
                    {reportData.complaints?.filter(c => c.status === 'in progress')?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">In Progress</div>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <div className="text-2xl font-bold text-green-600">
                    {reportData.complaints?.filter(c => c.status === 'resolved')?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600">Resolved</div>
                </div>
              </div>
            </div>

            {/* Detailed Complaints */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Detailed Complaints</h3>
              {reportData.complaints && reportData.complaints.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-300 px-4 py-2 text-left">Complaint/Feedback ID</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Submitted By</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Date Submitted</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Category</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Description/Details</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Resolution Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Staff/Teacher</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.complaints.map((complaint, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2 font-medium">
                            {complaint.id || `CF-${index + 1}`}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {complaint.submittedBy || "Anonymous"}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {new Date(complaint.dateSubmitted || complaint.createdAt).toLocaleDateString('en-GB')}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {complaint.category || "General"}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 max-w-xs">
                            <div className="truncate" title={complaint.description || complaint.details}>
                              {complaint.description || complaint.details || "No details provided"}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(complaint.status)}`}>
                              {complaint.status || "Pending"}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {complaint.resolutionDate ? 
                              new Date(complaint.resolutionDate).toLocaleDateString('en-GB') : 
                              "N/A"
                            }
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {complaint.staffTeacher || complaint.assignedTo || "Unassigned"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No complaints found
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No report data available</p>
          </div>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewComplaintReportModal;